/**
 * MultiSelect Component
 *
 * A multi-select dropdown with search, chips display, and individual item removal.
 */

import { useState, useRef, useEffect } from 'react';
import * as Popover from '@radix-ui/react-popover';
import * as Checkbox from '@radix-ui/react-checkbox';
import { Check, ChevronDown, X, Search } from 'lucide-react';

import { cn } from '@/utils/cn';

// ============================================
// Types
// ============================================

export interface MultiSelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface MultiSelectProps {
  /** Select options */
  options: MultiSelectOption[];
  /** Selected values */
  value?: string[];
  /** Change handler */
  onChange?: (values: string[]) => void;
  /** Placeholder text */
  placeholder?: string;
  /** Disabled state */
  disabled?: boolean;
  /** Error state */
  error?: boolean;
  /** Label text */
  label?: string;
  /** Helper text */
  helperText?: string;
  /** Error message */
  errorMessage?: string;
  /** Required field */
  required?: boolean;
  /** Enable search */
  searchable?: boolean;
  /** Search placeholder */
  searchPlaceholder?: string;
  /** Maximum selections allowed */
  maxSelections?: number;
  /** Show clear all button */
  clearable?: boolean;
  /** Additional trigger class */
  className?: string;
  /** Name attribute for forms */
  name?: string;
  /** ID for label association */
  id?: string;
}

// ============================================
// MultiSelect Component
// ============================================

export function MultiSelect({
  options,
  value = [],
  onChange,
  placeholder = 'Select options',
  disabled = false,
  error = false,
  label,
  helperText,
  errorMessage,
  required = false,
  searchable = true,
  searchPlaceholder = 'Search...',
  maxSelections,
  clearable = true,
  className,
  name,
  id,
}: MultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  const selectId = id || name;
  const displayError = error || !!errorMessage;

  // Filter options based on search
  const filteredOptions = searchQuery
    ? options.filter((opt) =>
        opt.label.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : options;

  // Get selected option labels
  const selectedOptions = options.filter((opt) => value.includes(opt.value));

  // Toggle selection
  const toggleOption = (optionValue: string) => {
    if (value.includes(optionValue)) {
      onChange?.(value.filter((v) => v !== optionValue));
    } else {
      if (maxSelections && value.length >= maxSelections) {
        return;
      }
      onChange?.([...value, optionValue]);
    }
  };

  // Remove single selection
  const removeOption = (optionValue: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange?.(value.filter((v) => v !== optionValue));
  };

  // Clear all selections
  const clearAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange?.([]);
  };

  // Focus search input when popover opens
  useEffect(() => {
    if (open && searchable && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 0);
    }
    if (!open) {
      setSearchQuery('');
    }
  }, [open, searchable]);

  return (
    <div className="space-y-1.5">
      {label && (
        <label
          htmlFor={selectId}
          className="block text-sm font-medium text-foreground dark:text-foreground-dark"
        >
          {label}
          {required && <span className="ml-1 text-danger">*</span>}
        </label>
      )}

      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger asChild disabled={disabled}>
          <button
            type="button"
            id={selectId}
            className={cn(
              'flex min-h-10 w-full items-center justify-between rounded-md border px-3 py-2 text-sm',
              'bg-card dark:bg-card-dark',
              'border-border dark:border-border-dark',
              'text-foreground dark:text-foreground-dark',
              'focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
              'dark:focus:ring-offset-background-dark',
              'disabled:cursor-not-allowed disabled:opacity-50',
              displayError && 'border-danger focus:ring-danger',
              className
            )}
          >
            <div className="flex flex-1 flex-wrap gap-1">
              {selectedOptions.length === 0 ? (
                <span className="text-foreground-muted dark:text-foreground-dark-muted">
                  {placeholder}
                </span>
              ) : (
                selectedOptions.map((opt) => (
                  <span
                    key={opt.value}
                    className={cn(
                      'inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium',
                      'bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-400'
                    )}
                  >
                    {opt.label}
                    <button
                      type="button"
                      onClick={(e) => removeOption(opt.value, e)}
                      className="rounded-full p-0.5 hover:bg-primary-200 dark:hover:bg-primary-800/50"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))
              )}
            </div>

            <div className="flex items-center gap-1">
              {clearable && selectedOptions.length > 0 && (
                <button
                  type="button"
                  onClick={clearAll}
                  className="rounded-full p-1 hover:bg-secondary-100 dark:hover:bg-secondary-800"
                >
                  <X className="h-4 w-4 opacity-50" />
                </button>
              )}
              <ChevronDown className="h-4 w-4 opacity-50" />
            </div>
          </button>
        </Popover.Trigger>

        <Popover.Portal>
          <Popover.Content
            className={cn(
              'z-50 min-w-[var(--radix-popover-trigger-width)] rounded-md border shadow-md',
              'border-border dark:border-border-dark',
              'bg-card dark:bg-card-dark',
              'data-[state=open]:animate-in data-[state=closed]:animate-out',
              'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
              'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
              'data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2'
            )}
            sideOffset={4}
            align="start"
          >
            {/* Search Input */}
            {searchable && (
              <div className="border-b border-border p-2 dark:border-border-dark">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-foreground-muted dark:text-foreground-dark-muted" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder={searchPlaceholder}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className={cn(
                      'w-full rounded-md border-0 bg-transparent py-1.5 pl-8 pr-3 text-sm',
                      'text-foreground dark:text-foreground-dark',
                      'placeholder:text-foreground-muted dark:placeholder:text-foreground-dark-muted',
                      'focus:outline-none focus:ring-0'
                    )}
                  />
                </div>
              </div>
            )}

            {/* Options List */}
            <div className="max-h-60 overflow-y-auto p-1">
              {filteredOptions.length === 0 ? (
                <div className="py-6 text-center text-sm text-foreground-muted dark:text-foreground-dark-muted">
                  No options found
                </div>
              ) : (
                filteredOptions.map((option) => {
                  const isSelected = value.includes(option.value);
                  const isDisabled =
                    option.disabled ||
                    (!isSelected && maxSelections !== undefined && value.length >= maxSelections);

                  return (
                    <label
                      key={option.value}
                      className={cn(
                        'flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm',
                        'hover:bg-secondary-100 dark:hover:bg-secondary-800',
                        isDisabled && 'cursor-not-allowed opacity-50'
                      )}
                    >
                      <Checkbox.Root
                        checked={isSelected}
                        disabled={isDisabled}
                        onCheckedChange={() => !isDisabled && toggleOption(option.value)}
                        className={cn(
                          'flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                          'border-border dark:border-border-dark',
                          'data-[state=checked]:border-primary data-[state=checked]:bg-primary',
                          'focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
                          'disabled:cursor-not-allowed disabled:opacity-50'
                        )}
                      >
                        <Checkbox.Indicator>
                          <Check className="h-3 w-3 text-white" />
                        </Checkbox.Indicator>
                      </Checkbox.Root>
                      <span className="text-foreground dark:text-foreground-dark">
                        {option.label}
                      </span>
                    </label>
                  );
                })
              )}
            </div>

            {/* Footer with selection count */}
            {maxSelections && (
              <div className="border-t border-border p-2 text-xs text-foreground-muted dark:border-border-dark dark:text-foreground-dark-muted">
                {value.length} of {maxSelections} selected
              </div>
            )}
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>

      {/* Hidden inputs for form submission */}
      {name &&
        value.map((v) => <input key={v} type="hidden" name={name} value={v} />)}

      {(helperText || errorMessage) && (
        <p
          className={cn(
            'text-xs',
            displayError
              ? 'text-danger'
              : 'text-foreground-muted dark:text-foreground-dark-muted'
          )}
        >
          {errorMessage || helperText}
        </p>
      )}
    </div>
  );
}
